import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Trash2, X, Save, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_JOB = { title: '', location: '', employment_type: 'Full-Time', work_arrangement: 'On-site', salary_range: '', description: '', requirements: '', responsibilities: '', apply_link: 'mailto:contact@scoreintegratedsolutions.com', clearance: '', active: true };

function JobForm({ job, onSave, onCancel }) {
  const [form, setForm] = useState(job);
  const [saving, setSaving] = useState(false);
  const up = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#161616] border border-[#F2B705]/20 p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Job Title *</label>
          <Input value={form.title} onChange={e => up('title', e.target.value)} className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]" /></div>
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Location</label>
          <Input value={form.location} onChange={e => up('location', e.target.value)} className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]" /></div>
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Employment Type</label>
          <select value={form.employment_type} onChange={e => up('employment_type', e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 text-white text-sm h-10 px-3">
            <option>Full-Time</option><option>Part-Time</option><option>Contract</option>
          </select></div>
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Work Arrangement</label>
          <select value={form.work_arrangement} onChange={e => up('work_arrangement', e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 text-white text-sm h-10 px-3">
            <option>On-site</option><option>Remote</option><option>Hybrid</option>
          </select></div>
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Clearance</label>
          <Input value={form.clearance} onChange={e => up('clearance', e.target.value)} className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]" /></div>
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Salary Range</label>
          <Input value={form.salary_range} onChange={e => up('salary_range', e.target.value)} placeholder="e.g. $80,000 - $120,000" className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]" /></div>
      </div>
      <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Description *</label>
        <Textarea value={form.description} onChange={e => up('description', e.target.value)} rows={3} className="bg-transparent border-white/10 text-white text-sm focus:border-[#F2B705] resize-none" /></div>
      <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Requirements</label>
        <Textarea value={form.requirements} onChange={e => up('requirements', e.target.value)} rows={2} className="bg-transparent border-white/10 text-white text-sm focus:border-[#F2B705] resize-none" /></div>
      <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Responsibilities</label>
        <Textarea value={form.responsibilities} onChange={e => up('responsibilities', e.target.value)} rows={2} className="bg-transparent border-white/10 text-white text-sm focus:border-[#F2B705] resize-none" /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Apply Link / Email</label>
          <Input value={form.apply_link} onChange={e => up('apply_link', e.target.value)} className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]" /></div>
        <div className="flex items-end gap-3">
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={e => up('active', e.target.checked)} className="accent-[#F2B705]" />
            Active (visible on website)
          </label>
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={handleSave} disabled={saving || !form.title} className="bg-[#F2B705] text-black font-bold text-xs uppercase px-5 py-2.5 hover:bg-[#D9A404] flex items-center gap-2 disabled:opacity-50">
          <Save size={14} /> {saving ? 'Saving...' : 'Save Job'}
        </button>
        <button onClick={onCancel} className="border border-white/10 text-[#888] text-xs uppercase px-5 py-2.5 hover:text-white flex items-center gap-2">
          <X size={14} /> Cancel
        </button>
      </div>
    </div>
  );
}

export default function JobsManager() {
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null); // null, 'new', or job id
  const [editJob, setEditJob] = useState(null);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/jobs`, { withCredentials: true });
      setJobs(res.data);
    } catch { setJobs([]); }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleCreate = async (form) => {
    await axios.post(`${API}/api/admin/jobs`, form, { withCredentials: true });
    setEditing(null);
    fetchJobs();
  };

  const handleUpdate = async (form) => {
    await axios.put(`${API}/api/admin/jobs/${editing}`, form, { withCredentials: true });
    setEditing(null);
    fetchJobs();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job opening?')) return;
    await axios.delete(`${API}/api/admin/jobs/${id}`, { withCredentials: true });
    fetchJobs();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Job Openings</h1>
        <button onClick={() => { setEditing('new'); setEditJob(EMPTY_JOB); }} className="bg-[#F2B705] text-black font-bold text-xs uppercase px-5 py-2.5 hover:bg-[#D9A404] flex items-center gap-2">
          <Plus size={14} /> Add Job
        </button>
      </div>

      {editing === 'new' && (
        <div className="mb-6">
          <JobForm job={EMPTY_JOB} onSave={handleCreate} onCancel={() => setEditing(null)} />
        </div>
      )}

      <div className="space-y-3">
        {jobs.map(job => (
          <div key={job.id}>
            {editing === job.id ? (
              <JobForm job={editJob} onSave={handleUpdate} onCancel={() => setEditing(null)} />
            ) : (
              <div className="bg-[#161616] border border-white/10 p-5 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-sm font-semibold text-white truncate">{job.title}</h3>
                    {job.active ? (
                      <span className="flex items-center gap-1 text-[10px] text-green-400 uppercase"><Eye size={10} /> Active</span>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] text-[#555] uppercase"><EyeOff size={10} /> Inactive</span>
                    )}
                  </div>
                  <p className="text-xs text-[#666]">{job.location} &middot; {job.employment_type} &middot; {job.clearance}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => { setEditing(job.id); setEditJob(job); }} className="text-[#888] hover:text-[#F2B705] p-2"><Pencil size={14} /></button>
                  <button onClick={() => handleDelete(job.id)} className="text-[#888] hover:text-red-400 p-2"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {jobs.length === 0 && <p className="text-[#666] text-sm">No job openings yet. Click "Add Job" to create one.</p>}
      </div>
    </div>
  );
}
